import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, CircleCheck, CreditCard, FileText, MapPin,
  Rocket, TriangleAlert, Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import {
  DEAL_STAGES_V2,
  getLatestProposalForBusiness,
  isAuthDisabled,
  isMockGenerationEnabled,
} from '@/components/deal/mockDealData';
import { toast } from 'sonner';

const BLOCKER_ICONS = {
  proposal: FileText,
  billing: CreditCard,
  targeting: MapPin,
  flight: Rocket,
  creative: Video,
  contact: TriangleAlert,
};

export default function LaunchChecklist() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const envFlags = {
    authDisabled: isAuthDisabled(),
    mockGenerationEnabled: isMockGenerationEnabled(),
  };
  const proposal = deal.proposal || getLatestProposalForBusiness(deal.proposals?.[businessId] || []);
  const readiness = deal.launchReadinessByBusiness?.[businessId] || {
    allReady: false,
    blockers: [],
    readyItems: [],
    items: [],
    progressPct: 0,
  };

  useEffect(() => {
    deal.setCurrentStage('close');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!businessId) return;
    deal.refreshLaunchReadiness(businessId).catch((error) => {
      console.error('Failed to load launch readiness', error);
      toast.error('Could not load launch readiness');
    });
  }, [businessId, deal.refreshLaunchReadiness]);

  if (!proposal) {
    return (
      <>
        <DealHeader
          businessName={deal.businessProfile?.name}
          currentStage="close"
          stages={DEAL_STAGES_V2}
          onBack={() => navigate(-1)}
        />
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <h1 className="text-2xl font-semibold text-slate-950">Launch blockers</h1>
              <p className="mt-2 text-sm text-slate-600">
                A proposal must exist before launch readiness can be evaluated.
              </p>
              <Button onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/proposal`)} className="mt-5 rounded-xl bg-coral text-white hover:bg-coral/90">
                Open proposal
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const handleLaunch = async () => {
    if (!readiness.allReady) return;
    try {
      await deal.launchCampaignForBusiness(businessId);
      toast.success('Campaign launched');
      navigate(`/org/${orgSlug}/deal/${dealId}/live`);
    } catch (error) {
      console.error('Failed to launch campaign', error);
      toast.error(error?.message || 'Could not launch the campaign');
    }
  };

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="close"
        stages={DEAL_STAGES_V2}
        onBack={() => navigate(-1)}
      />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <Badge variant="outline" className={`rounded-full text-xs ${readiness.allReady ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
            {readiness.allReady ? 'Ready to launch' : `${readiness.blockers.length} launch blocker${readiness.blockers.length !== 1 ? 's' : ''}`}
          </Badge>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            {readiness.allReady ? 'Launch review complete' : 'Launch blockers'}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            {readiness.readyItems.length} of {readiness.items.length} requirements satisfied.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Progress value={readiness.progressPct} className="h-2 flex-1 bg-slate-100 [&>[data-slot=progress-indicator]]:bg-teal" />
          <span className="shrink-0 text-sm font-medium text-slate-600">{readiness.progressPct}%</span>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[1.2fr_0.9fr]">
          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-4 w-4 text-amber-600" />
                  <h2 className="text-base font-semibold text-slate-950">Blocking issues</h2>
                </div>

                <div className="mt-4 space-y-3">
                  {readiness.blockers.length > 0 ? readiness.blockers.map((blocker) => {
                    const Icon = BLOCKER_ICONS[blocker.key] || TriangleAlert;
                    return (
                      <div key={blocker.key} className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white">
                            <Icon className="h-4 w-4 text-amber-700" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-amber-900">{blocker.label}</p>
                            <p className="mt-1 text-sm text-amber-800">{blocker.detail}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}${blocker.resolvePath}`)}
                            className="rounded-xl bg-white"
                          >
                            {blocker.resolveLabel}
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5 text-sm text-emerald-800">
                      No blockers are currently preventing launch.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4 text-emerald-600" />
                  <h2 className="text-base font-semibold text-slate-950">Ready items</h2>
                </div>
                <div className="mt-4 space-y-3">
                  {readiness.readyItems.map((item) => (
                    <div key={item.key} className="flex items-start gap-3">
                      <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-950">{item.label}</p>
                        <p className="mt-0.5 text-sm text-slate-500">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <Rocket className="h-4 w-4 text-coral" />
                  <h2 className="text-base font-semibold text-slate-950">Launch action</h2>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  Campaign starts when you confirm.
                </p>
                <Button
                  onClick={handleLaunch}
                  disabled={!readiness.allReady}
                  className="mt-5 h-12 w-full rounded-xl bg-coral text-white hover:bg-coral/90"
                >
                  <Rocket className="h-4 w-4" />
                  Launch campaign
                </Button>
                {!readiness.allReady && (
                  <p className="mt-3 text-sm text-slate-500">
                    Resolve the blockers above to enable launch.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h2 className="text-base font-semibold text-slate-950">Environment</h2>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Auth</span>
                    <span className="text-sm font-medium text-slate-950">
                      {envFlags.authDisabled ? 'Disabled (testing)' : 'Standard'}
                    </span>
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Generation</span>
                    <span className="text-sm font-medium text-slate-950">
                      {envFlags.mockGenerationEnabled ? 'Mock mode' : 'Standard'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
